import { groupLifeService } from '@/services/group-life';
import {
    keepPreviousData,
    useInfiniteQuery,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

export const PME_TARIFACAO_PAGE_SIZE = 10;

export type PmeTarifacaoContext = 'proposal' | 'quote';

function usePmeTarifacaoRevision(
  policyId: string,
  context: PmeTarifacaoContext
) {
  const queryClient = useQueryClient();
  const revisionKey = ['pme-tarifacao-revision', context, policyId] as const;
  const { data: revision = 0 } = useQuery({
    queryKey: revisionKey,
    queryFn: () => queryClient.getQueryData<number>(revisionKey) ?? 0,
    initialData: () => queryClient.getQueryData<number>(revisionKey) ?? 0,
    staleTime: Infinity,
  });

  return revision;
}

/**
 * Infinite scroll da Tarifação por vida do PME — usado pelas telas de Análise
 * de Proposta (`context: 'proposal'`) e Análise de Cotação (`context:
 * 'quote'`).
 *
 * Cada página vem do endpoint agregador
 * `GET /portal/v1/group-life/{proposals|quotes}/:policyId/pricing` (vidas
 * destacadas + coberturas decodificadas do GroupPolicyCoverageData).
 */
export function usePmeTarifacao(
  policyId: string,
  context: PmeTarifacaoContext,
  search?: string,
  pageSize: number = PME_TARIFACAO_PAGE_SIZE
) {
  const revision = usePmeTarifacaoRevision(policyId, context);

  return useInfiniteQuery({
    // `search` na key → mudar o termo reinicia o infinite scroll do zero.
    queryKey: [
      'pme-tarifacao',
      context,
      policyId,
      revision,
      pageSize,
      search ?? '',
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await groupLifeService.getTarifacaoPage({
        policyId,
        context,
        pageNo: pageParam,
        pageSize,
        search,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao carregar a tarifação');
      }
      return response.data;
    },
    getNextPageParam: lastPage =>
      lastPage.meta.pageNumber < lastPage.meta.totalPages
        ? lastPage.meta.pageNumber + 1
        : undefined,
    // Ao trocar o termo de busca (nova queryKey), mantém as páginas anteriores
    // na tela enquanto a nova busca carrega — só o grid indica o refresh, sem
    // "piscar" a página inteira (KPIs/cards continuam com dados).
    placeholderData: keepPreviousData,
    staleTime: 0,
    enabled: Boolean(policyId),
  });
}

/**
 * Consulta uma página de tarifação para grades com paginação explícita.
 *
 * `staleTime` 0 é o default porque na ANÁLISE a tarifação muda a cada
 * recálculo. Em telas de dado imutável (apólice emitida) passe um tempo maior:
 * sem isso, voltar para uma página já visitada refaz a chamada, já que o cache
 * nasce obsoleto. O recálculo continua refletindo mesmo com `staleTime` alto —
 * ele invalida a query e bumpa a `revision`, que entra na `queryKey`.
 */
export function usePmeTarifacaoPage(
  policyId: string,
  context: PmeTarifacaoContext,
  pageNo: number,
  search?: string,
  pageSize: number = PME_TARIFACAO_PAGE_SIZE,
  /** Bucket das vidas (`__STIPULATOR__` ou id do sub); ausente = apólice toda. */
  targetId?: string,
  options?: { staleTime?: number }
) {
  const revision = usePmeTarifacaoRevision(policyId, context);

  return useQuery({
    queryKey: [
      'pme-tarifacao',
      context,
      policyId,
      revision,
      pageSize,
      search ?? '',
      pageNo,
      targetId ?? '',
    ],
    queryFn: async () => {
      const response = await groupLifeService.getTarifacaoPage({
        policyId,
        context,
        pageNo,
        pageSize,
        search,
        targetId,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao carregar a tarifação');
      }
      return response.data;
    },
    placeholderData: keepPreviousData,
    staleTime: options?.staleTime ?? 0,
    enabled: Boolean(policyId),
  });
}
